import type { CloudState } from '@/store/cloud-types';

/**
 * Extracts CloudState-compatible data from AppState for cloud persistence.
 * Script blocks are excluded and saved separately to Cloud Storage.
 *
 * Only includes project data resources that have been loaded from the cloud so
 * default empty arrays cannot overwrite real remote data.
 */
type ExtractCloudStateOptions = {
    includePersonnel?: boolean;
};

function applyPersonnelWriteAuthority(
    localState: Partial<CloudState>,
    options: ExtractCloudStateOptions = {},
): Partial<CloudState> {
    if (options.includePersonnel !== false) {
        return localState;
    }

    const stateWithoutPersonnel = { ...localState };
    delete stateWithoutPersonnel.personnel;
    delete stateWithoutPersonnel.castAssignments;
    return stateWithoutPersonnel;
}

export function extractCloudState(
    appState: CloudState,
    loaded: ReadonlySet<string>,
    options: ExtractCloudStateOptions = {},
): Partial<CloudState> {
    const { scriptBlocks, ...showStructureWithoutBlocks } = appState.showStructure;
    void scriptBlocks;

    const result: Partial<CloudState> = {
        projectMeta: appState.projectMeta,
        showStructure: showStructureWithoutBlocks as typeof appState.showStructure,
        rows: appState.rows,
        personnel: appState.personnel,
        castAssignments: appState.castAssignments,
        assets: appState.assets,
    };

    if (loaded.has('props')) {
        result.props = appState.props;
    }
    if (loaded.has('sets')) {
        result.sets = appState.sets;
    }
    if (loaded.has('sound')) {
        result.soundClips = appState.soundClips;
        result.soundCues = appState.soundCues;
        result.micAssignments = appState.micAssignments;
        result.micSwaps = appState.micSwaps;
        result.micPackConfig = appState.micPackConfig;
    }
    if (loaded.has('lighting')) {
        result.lightingCues = appState.lightingCues;
        result.followSpotCues = appState.followSpotCues;
        result.followSpots = appState.followSpots;
    }
    if (loaded.has('promptBook')) {
        result.cueAnchors = appState.cueAnchors;
        result.customCues = appState.customCues;
    }
    if (loaded.has('fly')) {
        result.flyCues = appState.flyCues;
    }
    if (loaded.has('projections')) {
        result.projectionCues = appState.projectionCues;
    }

    return applyPersonnelWriteAuthority(result, options);
}
